// Ajustes de seguridad de la firma (VLT-307): vaultex.settings key 'security'.
// mfaRequiredStaff: con esto activo, TODO rol no-cliente (ven datos de
// terceros) queda bloqueado al entrar hasta inscribir su segundo factor.
// Real: seguro por defecto (true). Demo: sin fricción por defecto (false),
// activable vía localStorage para probar la compuerta.
import { supabase, hasBackend } from '@/lib/supabase';
import { loadFailed } from './loadError';
import { sessionUser } from '@/lib/sessionUser';

export interface SecuritySettings {
  mfaRequiredStaff: boolean;
  /** VLT-064 (0072): minutos sin actividad antes de cerrar la sesión. */
  idleMinutesStaff: number;
  idleMinutesClient: number;
  /** Horas máximas de una sesión del equipo (el hook de token lo hace cumplir). */
  maxSessionHoursStaff: number;
}

export const IDLE_OPTIONS = [15, 30, 60, 120] as const;
export const MAX_HOURS_OPTIONS = [8, 12, 24] as const;
/** Clientes: 30 días (su celular no debe pedir la clave a diario) — fijo, igual que session_max_hours. */
export const CLIENT_MAX_SESSION_HOURS = 720;

const KEY = 'security';
const SESSION_DEFAULTS = { idleMinutesStaff: 30, idleMinutesClient: 60, maxSessionHoursStaff: 12 };
const REAL_DEFAULTS: SecuritySettings = { mfaRequiredStaff: true, ...SESSION_DEFAULTS };
const DEMO_DEFAULTS: SecuritySettings = { mfaRequiredStaff: false, ...SESSION_DEFAULTS };
const DEMO_LS = 'vaultex.demoSecurity';

export interface SecuritySettingsService {
  get(): Promise<SecuritySettings>;
  set(patch: Partial<SecuritySettings>): Promise<boolean>;
}

const real: SecuritySettingsService = {
  async get() {
    const { data, error } = await supabase.from('settings').select('value').eq('key', KEY).maybeSingle();
    // Fallar ABIERTO aquí dejaría entrar sin MFA a una firma que lo exige.
    if (error) loadFailed('security.get', error);
    return { ...REAL_DEFAULTS, ...((data as { value: Partial<SecuritySettings> } | null)?.value ?? {}) };
  },

  async set(patch) {
    const { data: auth } = await sessionUser();
    if (!auth.user) return false;
    const { data: prof } = await supabase.from('profiles').select('organization_id').eq('id', auth.user.id).maybeSingle();
    const orgId = (prof as { organization_id: string } | null)?.organization_id;
    if (!orgId) return false;
    const value = { ...(await real.get()), ...patch };
    const { error } = await supabase
      .from('settings')
      .upsert({ organization_id: orgId, key: KEY, value, updated_by: auth.user.id } as never, { onConflict: 'organization_id,key' });
    if (error) console.warn('[VaulTex] security.set:', error.message);
    return !error;
  },
};

const fixture: SecuritySettingsService = {
  get() {
    try {
      const raw = localStorage.getItem(DEMO_LS);
      return Promise.resolve({ ...DEMO_DEFAULTS, ...(raw ? (JSON.parse(raw) as Partial<SecuritySettings>) : {}) });
    } catch {
      return Promise.resolve({ ...DEMO_DEFAULTS });
    }
  },
  async set(patch) {
    const current = await fixture.get();
    localStorage.setItem(DEMO_LS, JSON.stringify({ ...current, ...patch }));
    return true;
  },
};

export const securitySettingsService: SecuritySettingsService = hasBackend ? real : fixture;
